/**
 * Central map: page cluster -> real, verifiable photographs already owned by
 * the firm (client work samples, registered-lawyer licences, company
 * registration, team photos). Every entry is a genuine photo from
 * src/assets — no stock, no synthetic imagery — so the visual evidence on
 * each page matches the E-E-A-T claims made in the copy.
 *
 * Presentational data only. Consumed by <PageGallery />.
 */

import notaryInfo from "@/assets/portfolio/notary-info.webp";
import notary2 from "@/assets/portfolio/notary-2.webp";
import notary3 from "@/assets/portfolio/notary-3.webp";
import notary4 from "@/assets/portfolio/notary-4.webp";
import translationInfo from "@/assets/portfolio/translation-info.webp";
import translationGov from "@/assets/portfolio/translation-gov.webp";
import mojTranslation from "@/assets/portfolio/moj-translation.webp";
import naatiInfo from "@/assets/portfolio/naati-info.webp";
import naatiBirth1 from "@/assets/portfolio/naati-birth-cert-1.webp";
import naatiName from "@/assets/portfolio/naati-name-change.webp";
import cidInfo from "@/assets/portfolio/cid-info.webp";
import cidItaly from "@/assets/portfolio/cid-italy.webp";
import cidSpain1 from "@/assets/portfolio/cid-spain-1.webp";
import cidCzech from "@/assets/portfolio/cid-czech.webp";
import serviceOverview from "@/assets/credentials/service-overview.webp";
import businessRegistration from "@/assets/credentials/business-registration-official.webp";
import teamBangkok from "@/assets/team/team-bangkok.webp";

export interface PageImage {
  src: string;
  /** Descriptive alt text — what the photo actually shows. */
  alt: string;
  /** Short caption rendered under the photo. */
  caption: string;
  width: number;
  height: number;
}

const NOTARY: PageImage[] = [
  {
    src: notaryInfo,
    alt: "ตัวอย่างเอกสารที่ผ่านการรับรองลายมือชื่อโดยทนายความผู้ทำคำรับรอง (Notarial Services Attorney)",
    caption: "ตัวอย่างงานรับรองลายมือชื่อโดยทนายความผู้ทำคำรับรองที่ขึ้นทะเบียนกับสภาทนายความในพระบรมราชูปถัมภ์",
    width: 1280,
    height: 1280,
  },
  {
    src: notary2,
    alt: "เอกสารมอบอำนาจ (Power of Attorney) ที่รับรองลายมือชื่อพร้อมตราประทับของทนายความผู้ทำคำรับรอง",
    caption: "หนังสือมอบอำนาจที่รับรองลายมือชื่อพร้อมตราประทับ สำหรับใช้กับหน่วยงานต่างประเทศ",
    width: 1080,
    height: 1080,
  },
  {
    src: notary3,
    alt: "หน้ารับรองคำแปลและลายมือชื่อของทนายความ พร้อมข้อความ Certified True Copy",
    caption: "หน้ารับรอง Certified True Copy ที่ใช้แนบกับสำเนาเอกสารสำคัญ",
    width: 1080,
    height: 1080,
  },
  {
    src: notary4,
    alt: "เอกสาร Affidavit ที่ผ่านการรับรองโดยทนายความผู้ทำคำรับรองในประเทศไทย",
    caption: "Affidavit / คำให้การเป็นหนังสือ ที่รับรองโดยทนายความผู้ทำคำรับรอง",
    width: 1080,
    height: 1080,
  },
];

const TRANSLATION: PageImage[] = [
  {
    src: translationInfo,
    alt: "ตัวอย่างคำแปลเอกสารราชการไทยพร้อมหน้ารับรองความถูกต้องของคำแปล",
    caption: "คำแปลเอกสารราชการพร้อมหน้ารับรองความถูกต้องของคำแปล",
    width: 1280,
    height: 1280,
  },
  {
    src: translationGov,
    alt: "คำแปลเอกสารที่จัดรูปแบบตามข้อกำหนดของกรมการกงสุล กระทรวงการต่างประเทศ",
    caption: "รูปแบบคำแปลที่จัดตามข้อกำหนดของกรมการกงสุล กระทรวงการต่างประเทศ",
    width: 1280,
    height: 1280,
  },
  {
    src: mojTranslation,
    alt: "คำแปลเอกสารสำหรับยื่นหน่วยงานกระบวนการยุติธรรมของไทย",
    caption: "งานแปลเอกสารสำหรับยื่นต่อหน่วยงานในกระบวนการยุติธรรม",
    width: 1280,
    height: 1280,
  },
  {
    src: naatiInfo,
    alt: "ตัวอย่างคำแปลรับรองโดยนักแปลที่ได้รับการรับรอง NAATI สำหรับใช้ในออสเตรเลีย",
    caption: "คำแปลรับรอง NAATI พร้อมตราประทับนักแปลที่ได้รับการรับรอง",
    width: 1280,
    height: 1280,
  },
];

const NAATI: PageImage[] = [
  {
    src: naatiInfo,
    alt: "หน้ารับรองคำแปล NAATI พร้อมหมายเลขผู้ปฏิบัติงานที่ขึ้นทะเบียน",
    caption: "หน้ารับรอง NAATI พร้อมหมายเลขผู้ปฏิบัติงานที่ขึ้นทะเบียน",
    width: 1280,
    height: 1280,
  },
  {
    src: naatiBirth1,
    alt: "สูติบัตรไทยที่แปลและรับรองโดยนักแปล NAATI สำหรับยื่นหน่วยงานออสเตรเลีย",
    caption: "สูติบัตรไทยแปลรับรอง NAATI สำหรับยื่นหน่วยงานออสเตรเลีย",
    width: 1080,
    height: 1080,
  },
  {
    src: naatiName,
    alt: "หนังสือสำคัญการเปลี่ยนชื่อตัวที่แปลรับรอง NAATI",
    caption: "หนังสือสำคัญการเปลี่ยนชื่อตัว แปลรับรอง NAATI",
    width: 1080,
    height: 1080,
  },
];

const LEGALIZATION: PageImage[] = [
  {
    src: translationGov,
    alt: "เอกสารที่จัดเตรียมเพื่อยื่นรับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ",
    caption: "ชุดเอกสารที่จัดเตรียมเพื่อยื่นรับรองนิติกรณ์ที่กรมการกงสุล",
    width: 1280,
    height: 1280,
  },
  {
    src: notaryInfo,
    alt: "เอกสารที่รับรองลายมือชื่อโดยทนายความก่อนนำเข้าขั้นตอนรับรองนิติกรณ์",
    caption: "ขั้นรับรองลายมือชื่อโดยทนายความ ก่อนเข้าสู่ขั้นรับรองนิติกรณ์",
    width: 1280,
    height: 1280,
  },
  {
    src: serviceOverview,
    alt: "ภาพรวมบริการเอกสารระหว่างประเทศของ Thai Notary Law & Service",
    caption: "ภาพรวมบริการเอกสารระหว่างประเทศครบวงจร",
    width: 1280,
    height: 720,
  },
];

const BACKGROUND: PageImage[] = [
  {
    src: cidInfo,
    alt: "ใบตรวจสอบประวัติอาชญากรรม (Police Clearance Certificate) ที่ออกโดยสำนักงานตำรวจแห่งชาติ",
    caption: "ใบตรวจสอบประวัติอาชญากรรมที่ออกโดยสำนักงานตำรวจแห่งชาติ",
    width: 1280,
    height: 1280,
  },
  {
    src: cidItaly,
    alt: "ใบตรวจสอบประวัติอาชญากรรมที่ผ่านการรับรองสำหรับยื่นประเทศอิตาลี",
    caption: "งานตรวจประวัติที่รับรองครบขั้นตอนสำหรับยื่นประเทศอิตาลี",
    width: 1080,
    height: 1080,
  },
  {
    src: cidSpain1,
    alt: "ใบตรวจสอบประวัติอาชญากรรมที่ผ่านการรับรองสำหรับยื่นประเทศสเปน",
    caption: "งานตรวจประวัติที่รับรองครบขั้นตอนสำหรับยื่นประเทศสเปน",
    width: 1080,
    height: 1080,
  },
  {
    src: cidCzech,
    alt: "ใบตรวจสอบประวัติอาชญากรรมที่ผ่านการรับรองสำหรับยื่นสาธารณรัฐเช็ก",
    caption: "งานตรวจประวัติที่รับรองครบขั้นตอนสำหรับยื่นสาธารณรัฐเช็ก",
    width: 1080,
    height: 1080,
  },
];

const GENERAL: PageImage[] = [
  {
    src: teamBangkok,
    alt: "ทีมทนายความและเจ้าหน้าที่ของ Thai Notary Law & Service ที่สำนักงาน",
    caption: "ทีมทนายความและเจ้าหน้าที่ผู้ดูแลงานเอกสารระหว่างประเทศ",
    width: 1280,
    height: 945,
  },
  {
    src: businessRegistration,
    alt: "หนังสือรับรองการจดทะเบียนนิติบุคคลของ Thai Notary Law & Service Co., Ltd.",
    caption: "หนังสือรับรองการจดทะเบียนนิติบุคคลของบริษัท",
    width: 1080,
    height: 1080,
  },
  {
    src: serviceOverview,
    alt: "ภาพรวมบริการรับรองเอกสาร แปลเอกสาร และนิติกรณ์ของสำนักงาน",
    caption: "ภาพรวมบริการรับรองเอกสาร แปลเอกสาร และนิติกรณ์",
    width: 1280,
    height: 720,
  },
];

/** Pillar/cluster category -> photo set. */
const BY_CATEGORY: Record<string, PageImage[]> = {
  notary: NOTARY,
  translation: TRANSLATION,
  naati: NAATI,
  legalization: LEGALIZATION,
  consular: LEGALIZATION,
  background: BACKGROUND,
  "police-clearance": BACKGROUND,
  "visa-marriage": GENERAL,
  visa: GENERAL,
  marriage: GENERAL,
  general: GENERAL,
};

/**
 * Deterministic photo set for a page. `seed` (usually the page slug) rotates
 * the order so sibling pages in the same cluster are not byte-identical.
 */
export function getPageImages(category: string | undefined, seed = "", limit = 3): PageImage[] {
  const pool = BY_CATEGORY[category ?? "general"] ?? GENERAL;
  if (pool.length === 0) return [];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 9973;
  const offset = pool.length ? h % pool.length : 0;
  const rotated = [...pool.slice(offset), ...pool.slice(0, offset)];
  return rotated.slice(0, Math.min(limit, pool.length));
}